// 暗号化・復号化ユーティリティ
import type { KeyPair, EncryptedData } from '../types/auth';

// Web Crypto APIを使用した鍵ペア生成
export async function generateKeyPair(): Promise<KeyPair> {
  const keyPair = await window.crypto.subtle.generateKey(
    {
      name: 'RSA-OAEP',
      modulusLength: 2048,
      publicExponent: new Uint8Array([1, 0, 1]),
      hash: 'SHA-256',
    },
    true,
    ['encrypt', 'decrypt']
  );

  const publicKey = await window.crypto.subtle.exportKey('spki', keyPair.publicKey);
  const privateKey = await window.crypto.subtle.exportKey('pkcs8', keyPair.privateKey);

  return {
    publicKey: arrayBufferToBase64(publicKey),
    privateKey: arrayBufferToBase64(privateKey),
  };
}

// データの暗号化（AES-GCM + RSA-OAEP）
export async function encryptData(data: string, publicKeyPem: string): Promise<EncryptedData> {
  // AES-256-GCM用の鍵を生成
  const aesKey = await window.crypto.subtle.generateKey(
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt']
  );

  // IV（初期化ベクター）を生成
  const iv = window.crypto.getRandomValues(new Uint8Array(12));

  // データをAESで暗号化
  const encodedData = new TextEncoder().encode(data);
  const encryptedData = await window.crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    aesKey,
    encodedData
  );

  // AES鍵をRSAで暗号化
  const publicKey = await importPublicKey(publicKeyPem);
  const aesKeyBuffer = await window.crypto.subtle.exportKey('raw', aesKey);
  const encryptedAesKey = await window.crypto.subtle.encrypt(
    { name: 'RSA-OAEP' },
    publicKey,
    aesKeyBuffer
  );

  return {
    data: arrayBufferToBase64(encryptedData),
    iv: arrayBufferToBase64(iv),
    salt: arrayBufferToBase64(encryptedAesKey), // RSAで暗号化されたAES鍵
  };
}

// データの復号化
export async function decryptData(encryptedData: EncryptedData, privateKeyPem: string): Promise<string> {
  try {
    // RSA秘密鍵でAES鍵を復号化
    const privateKey = await importPrivateKey(privateKeyPem);
    const encryptedAesKey = base64ToArrayBuffer(encryptedData.salt);
    const aesKeyBuffer = await window.crypto.subtle.decrypt(
      { name: 'RSA-OAEP' },
      privateKey,
      encryptedAesKey
    );

    // AES鍵をインポート
    const aesKey = await window.crypto.subtle.importKey(
      'raw',
      aesKeyBuffer,
      { name: 'AES-GCM' },
      false,
      ['decrypt']
    );

    // データをAESで復号化
    const iv = base64ToArrayBuffer(encryptedData.iv);
    const encryptedDataBuffer = base64ToArrayBuffer(encryptedData.data);
    const decryptedData = await window.crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      aesKey,
      encryptedDataBuffer
    );

    return new TextDecoder().decode(decryptedData);
  } catch (error) {
    console.error('復号化エラー:', error);
    throw new Error('データの復号化に失敗しました');
  }
}

// 公開鍵のインポート
async function importPublicKey(publicKeyPem: string): Promise<CryptoKey> {
  const publicKeyBuffer = base64ToArrayBuffer(publicKeyPem);
  return await window.crypto.subtle.importKey(
    'spki',
    publicKeyBuffer,
    {
      name: 'RSA-OAEP',
      hash: 'SHA-256',
    },
    false,
    ['encrypt']
  );
}

// 秘密鍵のインポート
async function importPrivateKey(privateKeyPem: string): Promise<CryptoKey> {
  const privateKeyBuffer = base64ToArrayBuffer(privateKeyPem);
  return await window.crypto.subtle.importKey(
    'pkcs8',
    privateKeyBuffer,
    {
      name: 'RSA-OAEP',
      hash: 'SHA-256',
    },
    false,
    ['decrypt']
  );
}

// ArrayBufferをBase64に変換
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

// Base64をArrayBufferに変換
function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

// 評価対象名の暗号化（セキュリティモード用）
export async function encryptProductTitle(title: string, publicKey: string): Promise<string> {
  if (!publicKey) {
    return title; // セキュリティモードが無効の場合はそのまま返す
  }

  try {
    const encrypted = await encryptData(title, publicKey);
    return `encrypted:${JSON.stringify(encrypted)}`;
  } catch (error) {
    console.error('評価対象名の暗号化エラー:', error);
    return title; // エラー時は元のタイトルを返す
  }
}

// 評価対象名の復号化（セキュリティモード用）
export async function decryptProductTitle(encryptedTitle: string, privateKey: string): Promise<string> {
  if (!encryptedTitle.startsWith('encrypted:') || !privateKey) {
    return encryptedTitle; // 暗号化されていない場合はそのまま返す
  }

  try {
    const encryptedData = JSON.parse(encryptedTitle.substring(10)) as EncryptedData;
    return await decryptData(encryptedData, privateKey);
  } catch (error) {
    console.error('評価対象名の復号化エラー:', error);
    return '[復号化エラー]'; // エラー時は分かりやすいメッセージを返す
  }
}

// セキュリティモードの状態チェック
export function isSecurityModeEnabled(): boolean {
  try {
    const settings = localStorage.getItem('evaloid-user-settings');
    if (settings) {
      const userSettings = JSON.parse(settings);
      return userSettings.securityMode === true;
    }
  } catch (error) {
    console.error('セキュリティモード設定の読み込みエラー:', error);
  }
  return false;
}

// 公開鍵の取得
export function getPublicKey(): string | null {
  try {
    const settings = localStorage.getItem('evaloid-user-settings');
    if (settings) {
      const userSettings = JSON.parse(settings);
      return userSettings.publicKey || null;
    }
  } catch (error) {
    console.error('公開鍵の読み込みエラー:', error);
  }
  return null;
}

// 秘密鍵の取得
export function getPrivateKey(): string | null {
  try {
    const settings = localStorage.getItem('evaloid-user-settings');
    if (settings) {
      const userSettings = JSON.parse(settings);
      return userSettings.privateKey || null;
    }
  } catch (error) {
    console.error('秘密鍵の読み込みエラー:', error);
  }
  return null;
}